import { useRef } from 'react';
import { useCopilotTool } from '@customerio/hydra-copilot/react';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { z } from 'zod';
import zodToJsonSchema from 'zod-to-json-schema';
import { AskUserQuestion } from './ask-user-question';

const optionSchema = z.object({
  label: z.string().describe('Display text for this option (1-5 words, concise).'),
  description: z.string().describe('Explanation of what this option means or what will happen if chosen.'),
});

const questionSchema = z.object({
  question: z
    .string()
    .describe(
      'The complete question to ask the user. Should be clear, specific, and end with a question mark. If multiSelect is true, phrase it accordingly.',
    ),
  multiSelect: z
    .boolean()
    .describe('Set to true to allow the user to select multiple options. Use when choices are not mutually exclusive.'),
  options: z
    .array(optionSchema)
    .min(2)
    .max(4)
    .describe(
      'The available choices (2-4 options). Each should be distinct. Do not include an "Other" option — it is added automatically.',
    ),
});

const requestSchema = z.object({
  questions: z.array(questionSchema).min(1).max(4).describe('Questions to ask the user (1-4 questions).'),
});

export type AskUserQuestionArgs = z.infer<typeof requestSchema>;
export type Question = z.infer<typeof questionSchema>;

export function AskUserQuestionTool() {
  const isV2 = useFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const resolveRef = useRef<((answer: string) => void) | null>(null);

  useCopilotTool({
    type: 'client',
    name: 'ask_user_question',
    isEnabled: isV2 === true,
    description: `
      Use this tool when you need to ask the user questions during the conversation.

      This allows you to:
      1. Gather user preferences or requirements
      2. Clarify ambiguous instructions
      3. Get decisions on implementation choices
      4. Offer choices to the user about what direction to take

      IMPORTANT: You MUST always provide meaningful, specific options for every question.
      Never ask a question where the only reasonable answer is "Something else" — always
      provide at least 2 concrete options that cover the most likely answers.

      A "Something else" option with a free-text input is automatically added to every
      question, so do not include an "Other" option yourself.

      Use multiSelect: true when the user can pick more than one answer.

      <sample-chat>
      User: Help me set up email authentication
      Model: ask_user_question({ questions: [{ question: "Which authentication method would you like to configure?", multiSelect: false, options: [{ label: "SPF", description: "Sender Policy Framework — specify which servers can send email for your domain" }, { label: "DKIM", description: "DomainKeys Identified Mail — add a cryptographic signature to outgoing messages" }, { label: "DMARC", description: "Domain-based Message Authentication — set policies for handling unauthenticated mail" }] }] })
      </sample-chat>

      <sample-chat>
      User: I want to create a new campaign
      Model: ask_user_question({ questions: [{ question: "What type of campaign would you like to create?", multiSelect: false, options: [{ label: "Newsletter", description: "A one-time email sent to a segment of your audience" }, { label: "Automated", description: "A triggered campaign that sends based on user behavior or attributes" }] }] })
      </sample-chat>
    `,
    requestSchema: zodToJsonSchema(requestSchema),
    handler() {
      return new Promise<string>((resolve) => {
        resolveRef.current = resolve;
      });
    },
    render({ args, status }) {
      return (
        <AskUserQuestion
          args={args as AskUserQuestionArgs}
          status={status}
          onAnswer={(answer) => {
            resolveRef.current?.(answer);
            resolveRef.current = null;
          }}
        />
      );
    },
  });

  return null;
}
